import { useCallback, useEffect, useState } from "react";

/**
 * The browser's own "install this application" offer, caught and kept (US-33.1).
 *
 * <p>Chromium fires `beforeinstallprompt` once, early, and expects the page either to let it show its
 * own bar or to keep the event and show the offer where it belongs. We keep it: the offer belongs on
 * the install screen and in the profile menu, next to the sentence explaining what installing gets you,
 * not in a strip at the bottom of the window that people dismiss without reading.
 *
 * <p>Safari fires nothing at all and has no API: on iOS installing is Share → Add to Home Screen, and
 * the only honest thing an application can do is say so. Hence {@link InstallState.platform}.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPlatform = "prompt" | "ios" | "unsupported";

export interface InstallState {
  /** True when the page is running as an installed application rather than in a browser tab. */
  installed: boolean;
  /** Whether the browser offered us its prompt, expects the iOS gesture, or does neither. */
  platform: InstallPlatform;
  /** Present only while the browser's own offer is held; calling it shows the browser's dialog. */
  install: (() => Promise<boolean>) | null;
}

function runningStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  // The second half is Safari's: iOS has no display-mode media query for home-screen apps.
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const agent = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; a Mac with a touch screen does not exist, so this is the test.
  return /iPad|iPhone|iPod/.test(agent) || (agent.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

export function useInstallPrompt(): InstallState {
  const [held, setHeld] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(runningStandalone);

  useEffect(() => {
    const onOffer = (event: Event) => {
      // Without this Chromium shows its own bar, which is the same offer in a worse place.
      event.preventDefault();
      setHeld(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setHeld(null);
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onOffer);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onOffer);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!held) {
      return false;
    }
    await held.prompt();
    const { outcome } = await held.userChoice;
    // The event is good for one showing, whatever the answer.
    setHeld(null);
    return outcome === "accepted";
  }, [held]);

  return {
    installed,
    platform: held ? "prompt" : isIos() ? "ios" : "unsupported",
    install: held ? install : null,
  };
}
